import React from "react";
import { PowerOff } from "lucide-react";

import { ListCard, ListEmpty } from "@/shared/components/ListCard";
import { Pane } from "@/shared/components/settings-atoms";
import { GhostButton } from "@/shared/components/ui-kit";
import { Switch } from "@/shared/components/ui/switch";
import { SECTION_HELP, SectionInfoButton, type ConfigSection } from "../ConfigSectionHelp";

/** A section's name and the ⓘ that explains it. */
export function SectionTitle({ section, onHelp }: {
  section: ConfigSection;
  onHelp: (section: ConfigSection) => void;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {SECTION_HELP[section].title}
      <SectionInfoButton section={section} onOpen={onHelp} />
    </span>
  );
}

/* One section of an agent's Settings, as the page beside its sidebar: what it
   is and does, its switch when it can be off, then its content — or, while it
   is off, what the agent goes without and the way to turn it back on. */
export default function Section({ section, onHelp, enabled, onToggle, action, children }: {
  section: ConfigSection;
  onHelp: (section: ConfigSection) => void;
  /** Given with `onToggle`, for a section that can be switched off. */
  enabled?: boolean;
  onToggle?: () => void;
  /** The header's control for a section without a switch. */
  action?: React.ReactNode;
  children?: React.ReactNode;
}) {
  const { title, summary, off } = SECTION_HELP[section];
  return (
    <Pane
      title={<SectionTitle section={section} onHelp={onHelp} />}
      desc={summary}
      action={onToggle ? <Switch checked={!!enabled} onCheckedChange={() => onToggle()} aria-label={title} /> : action}
    >
      <div className="mt-6">
        {enabled === false ? (
          <ListCard>
            <ListEmpty icon={PowerOff} title="Switched off" hint={off} action={<GhostButton onClick={onToggle}>Turn on</GhostButton>} />
          </ListCard>
        ) : children}
      </div>
    </Pane>
  );
}
